/* eslint-disable import/order */
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { NgModule, Type } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DelonACLModule } from '@delon/acl';
import { AlainThemeModule } from '@delon/theme';
// CurrencyPricePipe is provided by @delon/util and will be available globally

import { DateViewItemComponent } from './components/date-view-item/date-view-item.component';
import { GlobalTemplatesComponent } from './components/global-templates/global-templates.component';
import { LicenseInfoComponent } from './components/license-info/license-info.component';
import { AppPromoComponent } from './components/promo/promo.component';
import { QuinnyCampaignComponent } from './components/quinny-campaign/quinny-campaign.component';
import { ScrollableComponent } from './components/scrollable/scrollable.component';
import { TourStartComponent } from './components/tour-start/tour-start.component';
import { ValidPostingPeriodAuthFormComponent } from './components/valid-posting-period-auth/valid-posting-period-auth.component';
import { ViewItemComponent } from './components/view-item/view-item.component';
import { AutoFocusInputDirective } from './directives/auto-focus-input.directive';

import { DigitonlyDirective } from './directives/digitonly.directive';
import { MobileInputDirective } from './directives/mobile-input.directive';
import { NumberInputDirective } from './directives/number-input.directive';
import { AutoWidthHeaderDirective } from './directives/view/page-header.directive';
import { EditViewGuard } from './guards/edit-view.guard';
import { CurrencyWithSpacePipe } from './pipes/currency-with-space.pipe';
import {
  BirDatePipe,
  BirDateTimePipe,
  CustomDatePipe,
  CustomDateTimePipe
} from './pipes/custom-date.pipe';
import { RangePipe } from './pipes/range.pipe';
import { AppService } from './services/app.service';
import { ConfirmService } from './services/confirm.service';
import { IntroService } from './services/intro.service';
import { LicenseService } from './services/license.service';
import { MomentService } from './services/moment.service';
import { OdataService } from './services/odata.service';
import { ResizeService } from './services/resize.service';
import { ValidPostingPeriodCheckingService } from './services/valid-posting-period-checking.service';
import { SHARED_DELON_MODULES } from './shared-delon';
import { SHARED_ZORRO_MODULES } from './shared-zorro';
import { ResizableDirective } from './directives/resizable.directive';
import { UpgradePlanComponent } from '../components/upgrade-plan/upgrade-plan.component';
import { AutoCloseSelectDirective } from './directives/auto-close-select.directive';
import { CustomDrawerComponent } from './components/custom-drawer/custom-drawer.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CopyButtonAriaLabelDirective } from './directives/copy-button-aria-label.directive';
import { NzSwitchAriaLabelDirective } from './directives/nz-switch-aria-label.directive';
import { ReuseTabExtraAriaLabelDirective } from './directives/reuse-tab-extra-aria-label.directive';
import { NzSwitchFocusDirective } from './directives/nz-switch-focus.directive';
import { ColumnVisibilityService } from './services/column-visibility.service';
import { ProtectedCurrencyPipe } from './pipes/protected-currency.pipe';
import { StockQuantityControlService } from './services/stock-quantity-control.service';
import { DevExtremeConfigService } from './services/devextreme-config.service';
// #region third libs

const THIRDMODULES: Array<Type<void>> = [];

// #endregion

// #region your componets & directives

const STANDALONE_COMPONENTS: Array<Type<void>> = [
  ScrollableComponent,
  UpgradePlanComponent,
  ViewItemComponent,
  DateViewItemComponent
];

const COMPONENTS: Array<Type<void>> = [
  LicenseInfoComponent,
  TourStartComponent,
  AppPromoComponent,
  QuinnyCampaignComponent,
  CustomDrawerComponent,
  GlobalTemplatesComponent,
  ValidPostingPeriodAuthFormComponent
];

const DIRECTIVES: Array<Type<void>> = [
  NumberInputDirective,
  MobileInputDirective,
  DigitonlyDirective,
  AutoFocusInputDirective,
  AutoWidthHeaderDirective,
  AutoCloseSelectDirective,
  ResizableDirective,
  CopyButtonAriaLabelDirective,
  NzSwitchAriaLabelDirective,
  NzSwitchFocusDirective,
  ReuseTabExtraAriaLabelDirective
];
const PIPES: Array<Type<void>> = [
  RangePipe,
  CurrencyWithSpacePipe,
  CustomDatePipe,
  CustomDateTimePipe,
  BirDatePipe,
  BirDateTimePipe,
  ProtectedCurrencyPipe
];

const SERVICES: Array<Type<void>> = [
  EditViewGuard,
  IntroService,
  ConfirmService,
  LicenseService,
  AppService,
  OdataService,
  MomentService,
  ValidPostingPeriodCheckingService,
  ResizeService,
  StockQuantityControlService,
  DevExtremeConfigService
];
// #endregion

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    DragDropModule,
    AlainThemeModule.forChild(),
    NzIconModule,
    ...SHARED_DELON_MODULES,
    ...SHARED_ZORRO_MODULES,
    // third libs
    ...THIRDMODULES,
    ...STANDALONE_COMPONENTS
  ],
  providers: [...PIPES, ...SERVICES, CurrencyPipe, DatePipe],
  declarations: [
    // your components
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    AlainThemeModule,
    DelonACLModule,
    ...SHARED_DELON_MODULES,
    ...SHARED_ZORRO_MODULES,
    // third libs
    ...THIRDMODULES,
    // your components
    ...COMPONENTS,
    ...STANDALONE_COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ]
})
export class SharedModule {
  constructor(private devExtremeConfigService: DevExtremeConfigService) {
    // Configure DevExtreme when SharedModule is loaded
    this.devExtremeConfigService.configureDevExtreme();
  }
}
